import {ArrUtil} from 'yougile-util'
import {Hub, Tag} from 'yougile-model'
import {Sync} from '@client/sync'
import {TablerIcons} from '@components/common/icon-tabler/icon-tabler'
import {NewStickerType} from '@components/sticker/my-stickers/my-stickers-store'
import {globals} from '@extensions/globals-base'
import {Project} from '@extensions/project'
import {Sticker} from '@extensions/sticker/sticker'

export interface Tree {
  project: Hub
  boards: Hub[]
}

export interface StickerLibrary {
  sticker: Tag
  tree: Tree[]
  isVisible: boolean
  isSystem: boolean
  boardsLength: number
  stickerName: string
}

export const getAllBoards = (): Hub[] => {
  const boards: Hub[] = []
  const companyId = globals.getCompanyId()
  const allProjects = Project.listProjects(companyId, 'not-archive')
  allProjects.map(project => {
    boards.push(...Project.listBoards(project))
  })
  return boards
}

const getAllStickers = (): Tag[] => {
  const box = Sync.getCurrentSandbox()
  const systemStickers = Sticker.list(box, {withSettings: true})
    .filter(s => Sticker.getController(s)?.isSystem)
    .sort((a, b) => {
      const aStickerName = Sticker.getName(a)
      const bStickerName = Sticker.getName(b)
      if (aStickerName < bStickerName) {
        return -1
      }
      return aStickerName > bStickerName ? 1 : 0
    })
  const customStickers = Sticker.list(box, {withSettings: true})
    .filter(s => !Sticker.getController(s)?.isSystem && !s.deleted)
    .sort((a, b) => {
      if (a.title < b.title) {
        return -1
      }
      return a.title > b.title ? 1 : 0
    })
  return [...systemStickers, ...customStickers]
}

export const getStickersLibrary = (): StickerLibrary[] => {
  const box = Sync.getCurrentSandbox()
  const allBoards = getAllBoards()
  const allStickers = getAllStickers()

  let stickers: StickerLibrary[] = allStickers.map(sticker => {
    const boards = allBoards.filter(board =>
      Sticker.isInBoardOrSettings(board, sticker)
    )

    const dirtyProjects = boards.map(board => box.getHub(board.location))
    const projects = ArrUtil.unique(dirtyProjects, x => x.id)

    const isSystem = Sticker.getController(sticker)?.isSystem

    return {
      sticker,
      stickerName: Sticker.getName(sticker) || '',
      isSystem,
      tree: projects.map(project => ({
        project,
        boards: boards.filter(board => board.location === project.id)
      })),
      isVisible: isSystem || Boolean(boards.length),
      boardsLength: boards.length
    }
  })

  stickers = stickers.sort((sticker1, sticker2) => {
    if (sticker1.isSystem && sticker2.isSystem) {
      const title1 = sticker1.stickerName || ''
      const title2 = sticker2.stickerName || ''

      return title1.localeCompare(title2)
    }

    const diffBySystem = Number(sticker2.isSystem) - Number(sticker1.isSystem)
    if (diffBySystem !== 0) {
      return diffBySystem * 1000
    }

    const diffBoardsLength = sticker2.boardsLength - sticker1.boardsLength
    if (diffBoardsLength !== 0) {
      return diffBoardsLength
    }

    const title1 = sticker1.stickerName || ''
    const title2 = sticker2.stickerName || ''

    return title1.localeCompare(title2)
  })

  return stickers
}

export const isStickerOnLastBoard = (sticker: Tag): boolean => {
  const boards = getAllBoards().filter(board =>
    Sticker.isInBoardOrSettings(board, sticker)
  )
  return boards.length === 1
}

export const scrollToElement = (
  element: HTMLElement,
  scrollContainer?: HTMLElement
): void => {
  if (scrollContainer) {
    scrollContainer.scrollTo({
      top: element.offsetTop - scrollContainer.offsetTop,
      behavior: 'auto'
    })
  }
}

export const highlightElement = (element: HTMLElement): void => {
  element.classList.add('bg-warning-l100')
  setTimeout(() => {
    element.classList.remove('bg-warning-l100')
  }, 500)
}

export const getStickerIconByNewStickerType = (
  stickerType: NewStickerType
): TablerIcons => {
  switch (stickerType) {
    case 'string':
      return 'string'
    case 'sprint':
      return 'IconCustomCalendar'
    case 'text':
      return 'IconCircleLetterT'
    case 'numeric':
      return 'string'
  }
}
